import { motion } from "framer-motion";
import { Quote, BadgeCheck, Star, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";

export function Testimonials() {
  const { t } = useLanguage();

  const owner = {
    name: t("testimonials.owner1.name"),
    role: t("testimonials.owner1.role"),
    stat: t("testimonials.owner1.stat"),
    quotes: [
      t("testimonials.owner1.quote2"),
      t("testimonials.owner1.quote3"),
    ],
    highlights: [
      {
        label: t("testimonials.owner1.highlight1Label"),
        value: t("testimonials.owner1.highlight1Value"),
      },
      {
        label: t("testimonials.owner1.highlight2Label"),
        value: t("testimonials.owner1.highlight2Value"),
      },
      {
        label: t("testimonials.owner1.highlight3Label"),
        value: t("testimonials.owner1.highlight3Value"),
      },
    ],
  };

  const screenshots = [
    { src: "/img/客户评价1.webp", alt: t("testimonials.shot1Alt") },
    { src: "/img/客户评价2.webp", alt: t("testimonials.shot2Alt") },
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 overflow-hidden">
      {/* 背景装饰 */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-blue-200/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-200/30 rounded-full blur-3xl translate-x-1/3 translate-y-1/3 pointer-events-none" />

      <div className="container mx-auto px-4 relative">
        {/* 标题区 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 mb-4 px-6 py-2 bg-blue-100 rounded-full">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-blue-600 font-semibold">
              {t("testimonials.badge")}
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t("testimonials.title")}
            <span className="block text-blue-600 mt-2">
              {t("testimonials.titleHighlight")}
            </span>
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {t("testimonials.subtitle")}
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* 左侧：评价卡片 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-7 relative"
          >
            <div className="relative bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-10 overflow-hidden">
              {/* 装饰大引号 */}
              <Quote className="absolute -top-2 -right-2 w-32 h-32 text-blue-100" strokeWidth={1} />

              {/* 车主信息 */}
              <div className="relative flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                  {owner.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-xl font-bold text-gray-900">
                      {owner.name}
                    </h3>
                    <BadgeCheck className="w-5 h-5 text-blue-500" />
                  </div>
                  <p className="text-sm text-gray-500">{owner.role}</p>
                  <div className="flex gap-0.5 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-4 h-4 fill-yellow-400 text-yellow-400"
                      />
                    ))}
                  </div>
                </div>
                <div className="hidden sm:block text-right">
                  <p className="text-xs text-gray-500">
                    {t("testimonials.verified")}
                  </p>
                  <p className="text-sm font-semibold text-blue-600 mt-1">
                    {owner.stat}
                  </p>
                </div>
              </div>

              {/* 评价内容 */}
              <div className="relative space-y-5">
                {owner.quotes.map((quote, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.5 }}
                    className="relative pl-5 border-l-4 border-blue-200"
                  >
                    <p className="text-gray-700 text-base md:text-lg leading-relaxed whitespace-pre-line">
                      {quote}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* 亮点 */}
              <div className="relative grid grid-cols-3 gap-3 mt-8 pt-6 border-t border-gray-100">
                {owner.highlights.map((h, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1, duration: 0.4 }}
                    className="text-center bg-gradient-to-br from-blue-50 to-white rounded-xl p-3 border border-blue-100"
                  >
                    <p className="text-xs text-gray-500 mb-1">{h.label}</p>
                    <p className="text-sm md:text-base font-bold text-blue-700">
                      {h.value}
                    </p>
                  </motion.div>
                ))}
              </div>

              {/* 移动端展示行驶里程 */}
              <div className="sm:hidden mt-6 text-center bg-blue-50 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">
                  {t("testimonials.verified")}
                </p>
                <p className="text-sm font-semibold text-blue-600">
                  {owner.stat}
                </p>
              </div>
            </div>
          </motion.div>

          {/* 右侧：WhatsApp 截图 */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="lg:col-span-5"
          >
            <div className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">
                {t("testimonials.screenshotsTitle")}
              </h3>
              <p className="text-sm text-gray-500">
                {t("testimonials.screenshotsSubtitle")}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              {screenshots.map((shot, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + index * 0.15, duration: 0.6 }}
                  whileHover={{ y: -6 }}
                  className="group"
                >
                  {/* 手机外框 */}
                  <div className="relative mx-auto rounded-[2rem] bg-gradient-to-b from-gray-800 to-gray-900 p-2 shadow-2xl">
                    {/* 顶部刘海 */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-16 h-4 bg-gray-900 rounded-b-2xl z-10" />
                    {/* 截图 */}
                    <div className="relative aspect-[9/19.5] rounded-[1.5rem] overflow-hidden bg-white">
                      <img
                        src={shot.src}
                        alt={shot.alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
